import React, { useState } from "react"
import { useStateMachine } from "../../../context/stateMachine/StateMachineV2"
import { Form } from "@carrotfertility/carotene-core-x"
import useBenefit from "../../../dynamic-content/hooks/useGetBenefit"
import {
  useCheckRoutingInfoQueryCache,
  useUpdateRoutingInfo
} from "#/components/carrot-plans/shared/useQueryRoutingInfo"
import { FormattedMessage, defineMessage, useIntl } from "react-intl"
import { ButtonFooterLayout, ContinueButton, RoutingFlowTitle, StepLayout } from "#/components/carrot-plans/shared"
import { RoutingFlowCheckboxGroupExpandable } from "#/components/carrot-plans/shared/RoutingFlowCheckboxGroupExpandable"
import { useGetCategorizedMostHelpfulOptions } from "#/components/carrot-plans/hooks/useMostHelpfulCategorizedOptions"
import {
  categorizedOptionsJourneyMap,
  categorizedOptionsPreservationInterestMap,
  CategoryOption,
  FlattenedOption
} from "#/components/carrot-plans/most-helpful-options/categorizedOptions"
import { Journey } from "#/components/carrot-plans/types/journey"
import { isNcqaQualifiedJourneyStage, JourneyStage } from "#/components/carrot-plans/types/journeyStage"
import { getIsCheckInFlow } from "#/components/carrot-plans/shared/getRoutingFlowPath"
import { PreservationInterest } from "#/components/carrot-plans/types/preservationInterest"

function useAccordionExpanded(defaultValue: string | string[] | null, flattenedMostHelpfulOptions: FlattenedOption[]) {
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(() => {
    if (!Array.isArray(defaultValue)) return false

    const initialOptionValues = flattenedMostHelpfulOptions
      .filter((opt) => opt.isInitial && !opt.isCategory)
      .map((opt) => opt.value)

    return defaultValue.some((value) => !initialOptionValues.includes(value))
  })

  return { isAccordionExpanded, setIsAccordionExpanded }
}

function getCategorizedOptions(
  journey: Journey,
  journeyStage: JourneyStage,
  preservationInterest: PreservationInterest
): CategoryOption[] | undefined {
  if (journey === Journey.PRESERVATION && preservationInterest && journeyStage !== JourneyStage.EXPLORING) {
    return categorizedOptionsPreservationInterestMap[journeyStage]?.[preservationInterest]
  }

  return categorizedOptionsJourneyMap?.[journey]?.[journeyStage]
}

export default function MostHelpfulStep(): JSX.Element {
  const { data: benefit, isLoading: isBenefitLoading } = useBenefit()
  const { mutateAsync } = useUpdateRoutingInfo()
  const routingData = useCheckRoutingInfoQueryCache()
  const journey = routingData?.journey
  const routingMostHelpfulArray = routingData?.mostHelpfulNow
  const intl = useIntl()

  const isCheckInFlow = getIsCheckInFlow()

  function getDescription(): string {
    if (isNcqaQualifiedJourneyStage(routingData.journeyStage)) {
      return intl.formatMessage(
        defineMessage({
          defaultMessage: "Choose up to three options to help us better understand your near-term goals."
        })
      )
    }
    if (isCheckInFlow) {
      return intl.formatMessage(
        defineMessage({ defaultMessage: "Choose up to three options that feel like the top priority." })
      )
    }
    return intl.formatMessage(
      defineMessage({
        defaultMessage: "Choose up to three options that feel like the top priority, just to get things started."
      })
    )
  }

  const description = getDescription()

  const categorizedOptions = getCategorizedOptions(
    journey as Journey,
    routingData.journeyStage as JourneyStage,
    routingData?.preservationInterest as PreservationInterest
  )

  const { flatCategorizedOptions } = useGetCategorizedMostHelpfulOptions(categorizedOptions ?? [], routingData, benefit)
  const availableCategorizedOptions = new Set(flatCategorizedOptions.map((opt) => opt.value))

  // Handle setting default value when selected an option that is no longer available
  const availableCategorizedMostHelpfulDefaultValue = routingMostHelpfulArray?.filter((val) =>
    availableCategorizedOptions.has(val)
  )

  const defaultValue: string[] | null =
    availableCategorizedMostHelpfulDefaultValue?.length === 0 ? null : availableCategorizedMostHelpfulDefaultValue

  const { send } = useStateMachine(defaultValue ? "" : null)

  const { isAccordionExpanded, setIsAccordionExpanded } = useAccordionExpanded(defaultValue, flatCategorizedOptions)

  async function onClickContinue({ mostHelpfulNow }: { mostHelpfulNow: string[] }): Promise<void> {
    await mutateAsync({ mostHelpfulNow: mostHelpfulNow }, { onSuccess: () => send("") })
  }

  const header = intl.formatMessage({
    defaultMessage: "What are you most interested in?"
  })

  const MostHelpfulOptions = () => {
    return (
      <RoutingFlowCheckboxGroupExpandable
        maxSelections={3}
        controllerProps={{ rules: { required: intl.formatMessage({ defaultMessage: "Required" }) } }}
        aria-labelledby="mostHelpfulNow-label"
        name="mostHelpfulNow"
        flattenedOptions={isBenefitLoading ? [] : flatCategorizedOptions}
        isExpanded={isAccordionExpanded}
        onExpandedChange={setIsAccordionExpanded}
      />
    )
  }

  return (
    <>
      <RoutingFlowTitle title={header} />
      <StepLayout
        header={<span id="mostHelpfulNow-label">{header}</span>}
        description={description}
        formElements={
          <Form defaultValues={{ mostHelpfulNow: defaultValue ?? [] }} onSubmit={onClickContinue}>
            <MostHelpfulOptions />
            <ButtonFooterLayout
              continueButton={
                <ContinueButton>
                  <FormattedMessage defaultMessage="Continue" />
                </ContinueButton>
              }
            />
          </Form>
        }
      />
    </>
  )
}
